import React, { useState, useEffect, useCallback } from 'react'
import Layout from '../components/Layout'
import { kyotenKanriService } from '../services/kyotenKanriService'
import { useApiError } from '../../shared/hooks/useApiError'
import MessageDialog from '../../shared/ui/MessageDialog'
import Button from '../../shared/ui/Button'
import { Trash2, RotateCcw, ChevronDown, ChevronUp, Move } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// Sortable Kyoten Item Component
const SortableKyotenItem = ({ kyoten, onUpdate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: kyoten.kyotenId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="bg-white border-gray-300 border rounded p-3 flex items-center space-x-3 cursor-move transition-all"
      {...attributes}
      {...listeners}
    >
      {/* Drag Handle */}
      <div className="w-10 h-10 bg-gray-700 rounded flex items-center justify-center cursor-move flex-shrink-0">
        <Move className="w-5 h-5 text-white" />
      </div>

      {/* Branch Name Input */}
      <input
        type="text"
        value={kyoten.kyotenName}
        onChange={(e) => onUpdate(kyoten.kyotenId, 'kyotenName', e.target.value)}
        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm font-medium h-10"
        placeholder="拠点名"
        onClick={(e) => e.stopPropagation()}
        data-testid={`kyoten-name-input-${kyoten.kyotenId}`}
      />

      {/* Branch Description Input */}
      <textarea
        value={kyoten.kyotenSetsumei}
        onChange={(e) => onUpdate(kyoten.kyotenId, 'kyotenSetsumei', e.target.value)}
        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm resize-none h-10"
        placeholder="拠点説明"
        rows="1"
        onClick={(e) => e.stopPropagation()}
        data-testid={`kyoten-description-input-${kyoten.kyotenId}`}
      />

      {/* Delete Button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onDelete(kyoten.kyotenId)
        }}
        className="w-10 h-10 bg-red-600 rounded flex items-center justify-center hover:bg-red-700 transition-colors"
        title="削除"
        data-testid={`delete-kyoten-button-${kyoten.kyotenId}`}
      >
        <Trash2 className="w-4 h-4 text-white flex-shrink-0" />
      </button>
    </li>
  )
}

const KyotenKanri = () => {
    const [existingKyotenList, setExistingKyotenList] = useState([]) // Existing branches with IDs
    const [newKyotenList, setNewKyotenList] = useState([]) // New branches without IDs
    const [deletedKyotenList, setDeletedKyotenList] = useState([]) // Deleted branches
    const [isLoading, setIsLoading] = useState(false)
    const [isRegistering, setIsRegistering] = useState(false)
    const [showDeletedBranches, setShowDeletedBranches] = useState(false)
    const [newKyoten, setNewKyoten] = useState({
        kyotenName: '',
        kyotenSetsumei: ''
    })
    const [maxKyotenId, setMaxKyotenId] = useState(0)
    const [showSuccessDialog, setShowSuccessDialog] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')

    // Error handling
    const { showErrorDialog, errorMessage, handleApiError, handleApiResponse, closeErrorDialog } = useApiError()

    // Drag and drop for active branches (combine existing and new)
    const allActiveKyoten = [...existingKyotenList, ...newKyotenList]

    // dnd-kit sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    // Handle drag end
    const handleDragEnd = (event) => {
        const { active, over } = event

        if (active.id !== over?.id) {
            const oldIndex = allActiveKyoten.findIndex(kyoten => kyoten.kyotenId === active.id)
            const newIndex = allActiveKyoten.findIndex(kyoten => kyoten.kyotenId === over.id)

            const newOrder = arrayMove(allActiveKyoten, oldIndex, newIndex)
            
            // Add order index to each item
            const orderedList = newOrder.map((kyoten, index) => ({
                ...kyoten,
                orderIndex: index + 1
            }))
            
            // Separate the reordered list back into existing and new
            const existing = orderedList.filter(kyoten => !newKyotenList.some(newKyoten => newKyoten.kyotenId === kyoten.kyotenId))
            const newOnes = orderedList.filter(kyoten => newKyotenList.some(newKyoten => newKyoten.kyotenId === kyoten.kyotenId))
            
            setExistingKyotenList(existing)
            setNewKyotenList(newOnes)
        }
    }

    const loadKyotenList = useCallback(async () => {
        try {
            setIsLoading(true)
            const response = await kyotenKanriService.getKyotenList()

            if (handleApiResponse(response, '拠点一覧の取得に失敗しました。')) {
                return
            }

            const allKyoten = response?.kyotenList || []
            const activeKyoten = allKyoten.filter(kyoten => kyoten.deleteFlg === '0' || kyoten.deleteFlg === 0)
            const deletedKyoten = allKyoten.filter(kyoten => kyoten.deleteFlg === '1' || kyoten.deleteFlg === 1)

            // Add order index to existing branches
            const activeKyotenWithOrder = activeKyoten.map((kyoten, index) => ({
                ...kyoten,
                orderIndex: index + 1
            }))
            
            setExistingKyotenList(activeKyotenWithOrder)
            setNewKyotenList([]) // Clear new branches after loading
            setDeletedKyotenList(deletedKyoten)

            // Find max ID for new branches
            const maxId = Math.max(...allKyoten.map(k => parseInt(k.kyotenId) || 0), 0)
            setMaxKyotenId(maxId)

        } catch (error) {
            console.error('Failed to load kyoten list:', error)
            handleApiError(error, '拠点一覧の読み込みに失敗しました。')
        } finally {
            setIsLoading(false)
        }
    }, [handleApiResponse, handleApiError])

    useEffect(() => {
        loadKyotenList()
    }, [loadKyotenList])

    const handleAddKyoten = () => {
        if (!newKyoten.kyotenName.trim()) {
            handleApiError(new Error('拠点名を入力してください。'), '入力エラー')
            return
        }

        const newKyotenItem = {
            kyotenId: (maxKyotenId + 1).toString(), // Use maxId + 1 for new branches
            kyotenName: newKyoten.kyotenName,
            kyotenSetsumei: newKyoten.kyotenSetsumei,
            deleteFlg: '0',
            orderIndex: allActiveKyoten.length + 1 // Add to the end of the list
        }

        setNewKyotenList(prev => [...prev, newKyotenItem])
        setMaxKyotenId(prev => prev + 1)
        setNewKyoten({ kyotenName: '', kyotenSetsumei: '' })
    }

    const handleDeleteKyoten = (kyotenId) => {
        // Check if it's a new branch or existing branch
        const isNewBranch = newKyotenList.some(kyoten => kyoten.kyotenId === kyotenId)
        
        if (isNewBranch) {
            // Remove from new branches list
            setNewKyotenList(prev => prev.filter(kyoten => kyoten.kyotenId !== kyotenId))
        } else {
            // Remove from existing branches and add to deleted list
            setExistingKyotenList(prev => prev.filter(kyoten => kyoten.kyotenId !== kyotenId))
            const deletedKyoten = existingKyotenList.find(kyoten => kyoten.kyotenId === kyotenId)
            if (deletedKyoten) {
                setDeletedKyotenList(prev => [...prev, { ...deletedKyoten, deleteFlg: '1' }])
            }
        }
    }

    const handleRestoreKyoten = (kyotenId) => {
        const restoredKyoten = deletedKyotenList.find(kyoten => kyoten.kyotenId === kyotenId)
        if (restoredKyoten) {
            setDeletedKyotenList(prev => prev.filter(kyoten => kyoten.kyotenId !== kyotenId))
            setExistingKyotenList(prev => [...prev, { ...restoredKyoten, deleteFlg: '0' }])
        }
    }

    const handleUpdateKyoten = (kyotenId, field, value) => {
        // Check if it's a new branch or existing branch
        const isNewBranch = newKyotenList.some(kyoten => kyoten.kyotenId === kyotenId)
        
        if (isNewBranch) {
            setNewKyotenList(prev =>
                prev.map(kyoten =>
                    kyoten.kyotenId === kyotenId
                        ? { ...kyoten, [field]: value }
                        : kyoten
                )
            )
        } else {
            setExistingKyotenList(prev =>
                prev.map(kyoten =>
                    kyoten.kyotenId === kyotenId
                        ? { ...kyoten, [field]: value }
                        : kyoten
                )
            )
        }
    }

    const handleRegister = async () => {
        try {
            setIsRegistering(true)

            // Process each list separately
            const promises = []

            // Add new branches
            if (newKyotenList.length > 0) {
                promises.push(kyotenKanriService.addKyotenList(newKyotenList))
            }

            // Update existing branches
            if (existingKyotenList.length > 0) {
                promises.push(kyotenKanriService.updateKyotenList(existingKyotenList))
            }

            // Remove deleted branches
            if (deletedKyotenList.length > 0) {
                promises.push(kyotenKanriService.removeKyotenList(deletedKyotenList))
            }

            // Execute all operations
            if (promises.length > 0) {
                const responses = await Promise.all(promises)
                
                // Check for errors in any response
                for (const response of responses) {
                    if (handleApiResponse(response, '拠点の登録に失敗しました。')) {
                        return
                    }
                }
            }

            // Show success message
            setSuccessMessage('登録が完了しました。')
            setShowSuccessDialog(true)
            
            // Clear new branches list and reload data
            setNewKyotenList([])
            await loadKyotenList()

        } catch (error) {
            console.error('Failed to register kyoten:', error)
            handleApiError(error, '拠点の登録に失敗しました。')
        } finally {
            setIsRegistering(false)
        }
    }


    return (
        <Layout pageTitle='拠点管理'>
            <div className="bg-white p-6 mx-6 my-6 max-w-4xl mx-auto">

                {isLoading ? (
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <>
                        {/* Active Branches List */}
                        <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 mb-6">
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext items={allActiveKyoten.map(kyoten => kyoten.kyotenId)}>
                                    <ul className="space-y-2">
                                        {allActiveKyoten.map((kyoten) => (
                                            <SortableKyotenItem
                                                key={kyoten.kyotenId}
                                                kyoten={kyoten}
                                                onUpdate={handleUpdateKyoten}
                                                onDelete={handleDeleteKyoten}
                                            />
                                        ))}
                                    </ul>
                                </SortableContext>
                            </DndContext>
                        </div>

                        {/* Add New Branch Section */}
                        <div className="bg-pink-50 border border-gray-300 rounded-lg p-4 mb-6" data-testid="add-new-kyoten-section">
                            <div className="flex items-center space-x-3">
                                <input
                                    type="text"
                                    value={newKyoten.kyotenName}
                                    onChange={(e) => setNewKyoten(prev => ({ ...prev, kyotenName: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm font-medium h-10"
                                    placeholder="拠点名"
                                    data-testid="new-kyoten-name-input"
                                />
                                <textarea
                                    value={newKyoten.kyotenSetsumei}
                                    onChange={(e) => setNewKyoten(prev => ({ ...prev, kyotenSetsumei: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm resize-none h-10"
                                    placeholder="拠点説明"
                                    rows="1"
                                    data-testid="new-kyoten-description-input"
                                />
                                <Button
                                    onClick={handleAddKyoten}
                                    variant="secondary"
                                    size="md"
                                    data-testid="add-kyoten-button"
                                >
                                    追加
                                </Button>
                            </div>
                        </div>

                        {/* Register Button */}
                        <div className="text-center mb-6">
                            <Button
                                onClick={handleRegister}
                                variant="danger"
                                size="lg"
                                loading={isRegistering}
                                disabled={isRegistering}
                            >
                                登録する
                            </Button>
                        </div>

                        {/* Deleted Branches Section */}
                        {deletedKyotenList.length > 0 && (
                            <div className="border border-gray-300 rounded-lg overflow-hidden">
                                <button
                                    onClick={() => setShowDeletedBranches(!showDeletedBranches)}
                                    className="w-full bg-gray-700 text-white px-4 py-3 flex items-center justify-between hover:bg-gray-800 transition-colors"
                                >
                                    <span>削除済拠点</span>
                                    {showDeletedBranches ? (
                                        <ChevronUp className="w-5 h-5" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5" />
                                    )}
                                </button>

                                {showDeletedBranches && (
                                    <div className="bg-white p-4">
                                        <ul className="space-y-2">
                                            {deletedKyotenList.map((kyoten) => (
                                                <li
                                                    key={kyoten.kyotenId}
                                                    className="bg-gray-50 border border-gray-200 rounded p-3 flex items-center justify-between"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-gray-900">{kyoten.kyotenName}</div>
                                                        <div className="text-sm text-gray-600">{kyoten.kyotenSetsumei}</div>
                                                    </div>
                                                    <Button
                                                        onClick={() => handleRestoreKyoten(kyoten.kyotenId)}
                                                        variant="outline"
                                                        size="sm"
                                                        icon={RotateCcw}
                                                        data-testid={`restore-kyoten-button-${kyoten.kyotenId}`}
                                                    >
                                                        戻す
                                                    </Button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* Error Dialog */}
                <MessageDialog
                    isOpen={showErrorDialog}
                    onClose={closeErrorDialog}
                    title="エラー"
                    message={errorMessage}
                    type="error"
                />

                {/* Success Dialog */}
                <MessageDialog
                    isOpen={showSuccessDialog}
                    onClose={() => setShowSuccessDialog(false)}
                    title="成功"
                    message={successMessage}
                    type="success"
                />
            </div>
        </Layout>
    )
}

export default KyotenKanri
